<template>
    <aside class="nb-layout-sidebar layout-sidebar" @click="onMenuClick">
        <nav>
            <menu v-if="model" class="nb-layout-sidebar__main-menu" role="menu">
                <!-- Search -->
                <li class="nb-layout-sidebar__search">
                    <!-- magnifying glass icon -->
                </li>

                <li v-for="menu in model[0]?.items" :key="menu.id">
                    <NimbusButton
                        @click="handleMenuClick(menu)"
                        :class="{ 'active-menu': isParentMenuActive(menu) }"
                        class="text-white"
                        outlined
                        text
                    >
                        <template #icon>
                            <i :class="[menu.icon, getIconSizeClass(menu.icon)]"></i>
                        </template>
                    </NimbusButton>
                    <AppMenuTooltip :label="menu.label" />
                </li>

                <!-- Switch to Cumulus-->
                <li class="nb-layout-sidebar__switcher" v-can="'switch.to.cumulus'">
                    <NimbusButton
                        class="text-white"
                        outlined
                        text
                        @click="switchToCumulus()"
                    >
                        <template #icon>
                            <i class="ph ph-arrow-fat-line-left text-2xl"></i>
                        </template>
                    </NimbusButton>
                    <AppMenuTooltip label="Switch to Cumulus" />
                </li>
            </menu>

            <div class="nb-layout-sidebar__sub-menu">
                <div class="logo-container">
                    <router-link to="/">
                        <img
                            id="app-logo"
                            :src="smallLogo"
                            class="w-full h-full nb-object-contain"
                            alt="emersion-logo"
                        />
                    </router-link>
                </div>

                <div v-if="selectedMenu">
                    <label class="font-bold text-base">{{ selectedMenu.label }}</label>
                    <menu role="menu">
                        <li v-for="subMenu in selectedMenu?.items" :key="subMenu.id">
                            <router-link :to="subMenu.to" active-class="active-menu">
                                {{ subMenu.label }}
                            </router-link>
                        </li>
                    </menu>
                </div>
            </div>
        </nav>
    </aside>
</template>

<script>
import { useEUPStore } from '@/stores/eup-store';
import { computed, onMounted, ref, watchEffect } from 'vue';
import { getEndUserPortalHost } from '@/service/EndUserPortalService';
import HandoverToken from './service/HandOverToken';
import UserAuthenticationService from './service/UserAuthenticationService';
import AppMenuTooltip from '@/AppMenuTooltip.vue';
import { useRoute } from 'vue-router';
import { getTopLevelParent } from '@/utilities/routeUtils';
import NimbusButton from '@/components/v2/NimbusButton.vue';

export default {
    name: 'AppMenu',
    emits: ['menu-click', 'menuitem-click', 'root-menuitem-click'],
    props: {
        model: Array,
        layoutMode: String,
        active: Boolean,
        mobileMenuActive: Boolean,
    },
    methods: {
        getTopLevelParent,
        onMenuClick(event) {
            this.$emit('menu-click', event);
        },
        onMenuItemClick(event) {
            this.$emit('menuitem-click', event);
        },
        onRootMenuItemClick(event) {
            this.$emit('root-menuitem-click', event);
        },
    },
    components: {
        NimbusButton,
        AppMenuTooltip,
    },
    setup(props) {
        const route = useRoute();
        const eupStore = useEUPStore();
        const isEUP = ref(false);
        const selectedMenu = ref(null);

        onMounted(() => {
            // by default set the eup settings in state
            useEUPStore().setEUPSettings();
            isEUP.value = !!getEndUserPortalHost();
        });

        // Function to determine the active menu based on the route
        const setActiveMenuBasedOnRoute = () => {
            if (props.model && props.model.length > 0) {
                // Determine the top-level parent based on the current route
                const topLevelParent = getTopLevelParent(route, props.model[0]?.items);

                if (topLevelParent) {
                    // Find the active menu using the top-level parent information
                    const activeMenu = props.model[0]?.items.find(menu => {
                        return isParentMenuActive(menu);
                    });

                    selectedMenu.value = activeMenu || null;
                }
            }
        };

        watchEffect(() => {
            setActiveMenuBasedOnRoute()
        });

        const smallLogo = computed(() => {

            /**
             * @TODO Fix this based on new navigation icon
             * This will be fix prior to the changes on the setup wizard
             */
            if (eupStore.eupSettings.sp_account_id == "5273849") {
                return 'assets/layout/images/TAG-logo.png';
            }

            if (eupStore.eupSettings.sp_account_id == "5647430") {
                return 'assets/layout/images/ezicom-logo.png';
            }
            
            return eupStore.eupSettings.public_path
                ? eupStore.eupSettings.public_path.small_logo
                : 'assets/layout/images/emersion-logo-white.png';
        });

        const switchToCumulus = () => {
            let handoverToken = new HandoverToken();
            let userService = new UserAuthenticationService();

            handoverToken
                .requestHandOver(userService.getBearer())
                .then((res) => {
                   window.open(process.env.VUE_APP_CUMULUS_URL + '?handover_token=' + res.data.handover_token,'_blank')
                })
                .catch(() => {
                    this.$router.push('/error');
                });
        };

        const handleMenuClick = (menu) => {
            selectedMenu.value = menu;
        };

        const isParentMenuActive = (menu) => {
            // return menu.items?.some(subMenu => route.path === subMenu.to);
            if (route.meta?.pageGroupKey === menu.page_group_key) {
                return true;
            }

            // Check submenus if they exist
            if (menu.items && menu.items.length > 0) {
                return menu.items.some(subMenu => {
                    return subMenu.to === route.path;
                });
            }

            return false;
        };

        // This is to control the icon sizing, since PrimeIcons is much bigger compared to phosphor
        const getIconSizeClass = (icon) => {
            if (icon.startsWith('ph')) {
                return 'text-2xl inline-block';
            } else if (icon.startsWith('pi')) {
                return 'text-xl';
            } else if (icon.startsWith('nb')) {
                return 'text-2xl bg-white';
            }
            return '';
        }

        return {
            smallLogo,
            isEUP,
            switchToCumulus,
            selectedMenu,
            handleMenuClick,
            isParentMenuActive,
            getIconSizeClass,
        };
    },
};
</script>
